import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { X } from 'lucide-react'; // optional: use any icon library or inline SVG

const ReportModal = ({
  show,
  onClose,
  onReport,
  isLoading,
  isReported,
  reportOptions,
  optionsLoading,
  handleOnChange,
  commentsRef,
  reportMessage,
  validationError,
  DAY_LIMIT,
}) => {
  if (!show) return null;

  const isOtherSelected = reportOptions?.some((opt) => opt.key === 'other' && opt.checked);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" style={{ zIndex: 1000 }}>
      <div className="relative bg-[var(--q3-surface-default)] rounded-xl shadow-xl max-w-md w-fit p-6">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--q3-neutral-default)] hover:text-[var(--q3-neutral-dimmer)] cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-[var(--q3-neutral-default)]">Please tell us about the issue</h2>
          <p className="text-sm text-[var(--q3-danger-normal)]">
            You can report {reportMessage.count} questions in next {reportMessage.daysLeft} days. This limit will reset
            every {DAY_LIMIT} days.
          </p>
        </div>

        {/* Body */}
        {!isReported ? (
          <form className="space-y-4">
            {/* Reasons */}
            <div>
              <label className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">
                Select all possible reason(s):
              </label>
              {optionsLoading ? (
                <p className="text-sm text-[var(--q3-neutral-light)]">Loading reasons…</p>
              ) : (
                reportOptions?.map((reportOption, index) => (
                  <div key={reportOption.key ?? index} className="cursor-pointer flex items-center space-x-2">
                    <Checkbox
                      id={`reason-${index}`}
                      checked={reportOption.checked}
                      onCheckedChange={(value) => handleOnChange(value, index)}
                      className="cursor-pointer rounded-[4px] data-[state=checked]:bg-[var(--q3-accent-normal)] border-[var(--q3-stroke-normal)] hover:border-[var(--q3-accent-normal)] focus-visible:ring-[var(--q3-accent-normal)]"
                    />
                    <label
                      htmlFor={`reason-${index}`}
                      className="cursor-pointer text-sm w-full text-[var(--q3-neutral-default)] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {reportOption.label}
                    </label>
                  </div>
                ))
              )}
            </div>

            {/* Comments */}
            <div>
              <label htmlFor="comments" className="block text-sm font-medium text-[var(--q3-neutral-default)] mb-2">
                Please elaborate the error(s) {isOtherSelected ? <span className="text-[var(--q3-danger-normal)]">(required)</span> : '(optional)'}
              </label>
              <textarea
                ref={commentsRef}
                id="comments"
                rows="3"
                placeholder="Please describe the issue."
                className="w-full rounded-md border border-[var(--q3-stroke-normal)] p-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--q3-accent-normal)]"
              />
            </div>

            {/* Validation Error */}
            {validationError && <p className="text-[var(--q3-danger-normal)] text-sm">{validationError}</p>}

            {/* Limit / Error Message */}
            {!reportMessage.success && <p className="text-red-500 text-sm">{reportMessage.message}</p>}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="px-4 py-2 rounded-md text-sm">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onReport}
                disabled={isLoading || optionsLoading}
                className="px-4 py-2 rounded-md disabled:opacity-50 text-sm"
              >
                Report
              </Button>
            </div>
          </form>
        ) : (
          <div className="text-center text-[var(--q3-neutral-default)]">
            <p>Thank you for reporting the error.</p>
            <p>Our content team will look into it and resolve the errors (if any).</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
